import { useState } from "react";
import { 
  X, ArrowRight, ArrowLeft, CheckCircle, Globe, MapPin, AlertTriangle,
  FileText, Building, ExternalLink, Info
} from "lucide-react";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const RegulatoryChecker = ({ onClose }) => {
  const [step, setStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [formData, setFormData] = useState({
    country: "",
    export_markets: [],
    industry: "",
    product_category: "",
    operation_type: ""
  });

  const countries = [
    { id: "jamaica", name: "Jamaica", flag: "🇯🇲" },
    { id: "trinidad", name: "Trinidad & Tobago", flag: "🇹🇹" },
    { id: "barbados", name: "Barbados", flag: "🇧🇧" },
    { id: "bahamas", name: "Bahamas", flag: "🇧🇸" },
    { id: "guyana", name: "Guyana", flag: "🇬🇾" },
    { id: "usa", name: "United States", flag: "🇺🇸" },
    { id: "canada", name: "Canada", flag: "🇨🇦" },
    { id: "uk", name: "United Kingdom", flag: "🇬🇧" }
  ];

  const exportMarkets = [
    { id: "usa", name: "United States", flag: "🇺🇸" },
    { id: "eu", name: "European Union", flag: "🇪🇺" },
    { id: "uk", name: "United Kingdom", flag: "🇬🇧" },
    { id: "canada", name: "Canada", flag: "🇨🇦" },
    { id: "caricom", name: "CARICOM", flag: "🌴" },
    { id: "asia", name: "Asia Pacific", flag: "🌏" },
    { id: "none", name: "No Export (Local Only)", flag: "📍" }
  ];

  const industries = [
    { id: "food_processing", name: "Food Processing & Manufacturing" },
    { id: "agriculture", name: "Agriculture & Farming" },
    { id: "seafood", name: "Seafood & Aquaculture" },
    { id: "beverages", name: "Beverages" },
    { id: "hospitality", name: "Hospitality & Food Service" },
    { id: "distribution", name: "Distribution & Logistics" },
    { id: "manufacturing", name: "Other Manufacturing" },
    { id: "services", name: "Professional Services" }
  ];

  const productCategories = [
    "Fresh Produce", "Processed Foods", "Dairy Products", "Meat & Poultry",
    "Seafood", "Beverages", "Bakery Products", "Confectionery",
    "Spices & Seasonings", "Canned/Preserved Foods", "Frozen Foods", "Other"
  ];

  const operationTypes = [
    { id: "manufacturer", name: "Manufacturer/Processor" },
    { id: "packer", name: "Packer/Packhouse" },
    { id: "exporter", name: "Exporter/Trader" },
    { id: "distributor", name: "Distributor/Wholesaler" },
    { id: "retailer", name: "Retailer/Food Service" },
    { id: "farm", name: "Farm/Primary Producer" }
  ];

  // Regulatory database (simplified - in production this would be an API)
  const regulatoryDatabase = {
    jamaica: {
      local: [
        { name: "Food and Drugs Act (FDA)", authority: "Ministry of Health", requirement: "Mandatory for all food businesses" },
        { name: "Public Health Act", authority: "Ministry of Health", requirement: "Food handler permits required" },
        { name: "Standards Act", authority: "Bureau of Standards Jamaica", requirement: "Product standards compliance" },
        { name: "Trade Board Licensing", authority: "Trade Board", requirement: "Required for importers/exporters" }
      ]
    },
    usa: {
      export: [
        { name: "FDA Registration", authority: "US FDA", requirement: "All food facilities exporting to US must register", critical: true },
        { name: "FSMA - Preventive Controls", authority: "US FDA", requirement: "Foreign suppliers must comply with FSVP", critical: true },
        { name: "HACCP Compliance", authority: "US FDA/USDA", requirement: "Required for seafood, juice, meat products", critical: true },
        { name: "Prior Notice of Imported Foods", authority: "US FDA", requirement: "Advance notification required for all food shipments" },
        { name: "Bioterrorism Act Registration", authority: "US FDA", requirement: "Annual registration renewal required" }
      ]
    },
    eu: {
      export: [
        { name: "EU Food Hygiene Regulations", authority: "European Commission", requirement: "HACCP-based food safety system required", critical: true },
        { name: "Novel Food Regulation", authority: "EFSA", requirement: "Authorization required for novel ingredients" },
        { name: "Food Contact Materials", authority: "European Commission", requirement: "Packaging compliance required" },
        { name: "Traceability Requirements", authority: "European Commission", requirement: "Full supply chain traceability mandatory", critical: true },
        { name: "Official Controls Regulation", authority: "Competent Authority", requirement: "Pre-export inspection may be required" }
      ]
    },
    uk: {
      export: [
        { name: "UK Food Standards", authority: "FSA", requirement: "HACCP-based food safety system required", critical: true },
        { name: "Export Health Certificate", authority: "Competent Authority", requirement: "Required for products of animal origin" },
        { name: "Labelling Requirements", authority: "FSA", requirement: "UK-specific labelling compliance" }
      ]
    },
    caricom: {
      export: [
        { name: "CARICOM Regional Standards", authority: "CROSQ", requirement: "Regional food standards compliance" },
        { name: "Certificate of Origin", authority: "Trade Ministry", requirement: "Required for preferential tariff access" }
      ]
    },
    canada: {
      export: [
        { name: "Safe Food for Canadians Act", authority: "CFIA", requirement: "Licensing and preventive controls required", critical: true },
        { name: "SFCR Compliance", authority: "CFIA", requirement: "Foreign establishments must meet requirements" }
      ]
    }
  };

  const toggleExportMarket = (marketId) => {
    if (marketId === 'none') {
      setFormData(prev => ({ ...prev, export_markets: ['none'] }));
    } else {
      setFormData(prev => ({
        ...prev,
        export_markets: prev.export_markets.includes(marketId)
          ? prev.export_markets.filter(m => m !== marketId)
          : [...prev.export_markets.filter(m => m !== 'none'), marketId]
      }));
    }
  };

  const analyzeRegulations = () => {
    setIsLoading(true);
    
    setTimeout(async () => {
      const regulations = [];
      
      // Local regulations
      const localRegs = regulatoryDatabase[formData.country]?.local || regulatoryDatabase.jamaica.local;
      localRegs.forEach(reg => {
        regulations.push({
          ...reg,
          type: "Local",
          market: countries.find(c => c.id === formData.country)?.name || "Jamaica"
        });
      });
      
      // Export regulations
      formData.export_markets.filter(m => m !== 'none').forEach(market => {
        const marketRegs = regulatoryDatabase[market]?.export || [];
        marketRegs.forEach(reg => {
          regulations.push({
            ...reg,
            type: "Export",
            market: exportMarkets.find(m => m.id === market)?.name || market
          });
        });
      });

      const analysisResult = {
        id: `reg-${Date.now()}`,
        country: formData.country,
        export_markets: formData.export_markets,
        industry: formData.industry,
        total_regulations: regulations.length,
        critical_requirements: regulations.filter(r => r.critical).length,
        regulations: regulations,
        recommendations: [
          "Engage with local regulatory authorities early in your compliance journey",
          "Consider HACCP certification as foundation for most export requirements",
          "Maintain documentation of compliance for all applicable regulations",
          "Work with an experienced compliance consultant for export market entry"
        ],
        next_steps: [
          "Review each applicable regulation in detail",
          "Assess current compliance status for each requirement",
          "Develop a compliance roadmap with priorities",
          "Contact our team for regulatory compliance support"
        ]
      };

      // Save to session
      const sessionId = localStorage.getItem("chatbot_session_id");
      if (sessionId) {
        try {
          await axios.post(`${API}/tools/session/save`, {
            session_id: sessionId,
            tool_name: "regulatory_checker",
            result: analysisResult
          });
        } catch (e) {
          console.error("Failed to save session:", e);
        }
      }

      setResult(analysisResult);
      setIsLoading(false);
    }, 1500);
  };

  const renderStep = () => {
    switch (step) {
      case 1:
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold mb-2">Where is your business located?</h3>
              <p className="text-sm text-muted-foreground mb-4">
                This determines which local regulations apply to your operations.
              </p>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {countries.map((country) => (
                <button
                  key={country.id}
                  onClick={() => { setFormData({ ...formData, country: country.id }); setStep(2); }}
                  className={`p-4 rounded-xl border-2 text-center transition-all hover:border-brand ${
                    formData.country === country.id ? 'border-brand bg-brand/5' : 'border-slate-200'
                  }`}
                >
                  <span className="text-2xl mb-2 block">{country.flag}</span>
                  <span className="font-medium text-sm">{country.name}</span>
                </button>
              ))}
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold mb-2">Which markets do you export to?</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Select all export markets to identify applicable regulations.
              </p>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {exportMarkets.map((market) => (
                <button
                  key={market.id}
                  onClick={() => toggleExportMarket(market.id)}
                  className={`p-4 rounded-xl border-2 text-center transition-all ${
                    formData.export_markets.includes(market.id) 
                      ? 'border-brand bg-brand/5' 
                      : 'border-slate-200 hover:border-brand/50'
                  }`}
                >
                  <span className="text-2xl mb-2 block">{market.flag}</span>
                  <span className="font-medium text-sm">{market.name}</span>
                </button>
              ))}
            </div>
            <div className="flex justify-between pt-4">
              <Button variant="outline" onClick={() => setStep(1)}>
                <ArrowLeft className="mr-2 h-4 w-4" /> Back
              </Button>
              <Button 
                onClick={() => setStep(3)} 
                className="bg-brand hover:bg-brand-dark"
                disabled={formData.export_markets.length === 0}
              >
                Continue <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold mb-2">What&apos;s your industry and operation?</h3>
              <p className="text-sm text-muted-foreground mb-4">
                This helps identify industry-specific regulations.
              </p>
            </div>
            
            {/* Industry */}
            <div>
              <label className="text-sm font-medium mb-2 block">Industry</label>
              <div className="grid grid-cols-2 gap-2">
                {industries.map((ind) => (
                  <button
                    key={ind.id}
                    onClick={() => setFormData({ ...formData, industry: ind.id })}
                    className={`p-3 rounded-lg border text-sm text-left transition-all ${
                      formData.industry === ind.id 
                        ? 'border-brand bg-brand text-white' 
                        : 'border-slate-200 hover:border-brand/50'
                    }`}
                  >
                    {ind.name}
                  </button>
                ))}
              </div>
            </div>

            {/* Operation Type */}
            <div>
              <label className="text-sm font-medium mb-2 block">Operation Type</label>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {operationTypes.map((op) => (
                  <button
                    key={op.id}
                    onClick={() => setFormData({ ...formData, operation_type: op.id })}
                    className={`p-3 rounded-lg border text-sm transition-all ${
                      formData.operation_type === op.id 
                        ? 'border-brand bg-brand text-white' 
                        : 'border-slate-200 hover:border-brand/50'
                    }`}
                  >
                    {op.name}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex justify-between pt-4">
              <Button variant="outline" onClick={() => setStep(2)}>
                <ArrowLeft className="mr-2 h-4 w-4" /> Back
              </Button>
              <Button 
                onClick={analyzeRegulations} 
                className="bg-brand hover:bg-brand-dark"
                disabled={isLoading || !formData.industry}
              >
                {isLoading ? "Analyzing..." : "Check Regulations"}
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  if (result) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-primary">Regulatory Exposure Report</h2>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="h-5 w-5" />
          </Button>
        </div>

        {/* Summary */}
        <div className="bg-brand/5 border border-brand/20 rounded-xl p-4 mb-6">
          <div className="flex items-center gap-3 mb-3">
            <Globe className="h-5 w-5 text-brand" />
            <span className="font-semibold">Regulatory Summary</span>
          </div>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-muted-foreground">Location:</span>
              <span className="ml-2 font-medium">{countries.find(c => c.id === result.country)?.name}</span>
            </div>
            <div>
              <span className="text-muted-foreground">Export Markets:</span>
              <span className="ml-2 font-medium">
                {result.export_markets.map(m => 
                  exportMarkets.find(em => em.id === m)?.name
                ).join(", ")}
              </span>
            </div>
            <div>
              <span className="text-muted-foreground">Total Regulations:</span>
              <span className="ml-2 font-bold text-brand">{result.total_regulations}</span>
            </div>
            <div>
              <span className="text-muted-foreground">Critical Requirements:</span>
              <span className="ml-2 font-bold text-red-600">{result.critical_requirements}</span>
            </div>
          </div>
        </div>

        {/* Regulations List */}
        <div className="mb-6">
          <h3 className="font-semibold mb-3 flex items-center gap-2">
            <FileText className="h-5 w-5 text-brand" />
            Applicable Regulations
          </h3>
          <div className="space-y-3 max-h-72 overflow-y-auto">
            {result.regulations.map((reg, index) => (
              <div 
                key={index} 
                className={`rounded-lg p-4 border ${
                  reg.critical ? 'bg-red-50 border-red-200' : 'bg-slate-50 border-slate-200'
                }`}
              >
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{reg.name}</span>
                      {reg.critical && (
                        <Badge className="bg-red-100 text-red-700 text-xs">Critical</Badge>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground">{reg.authority}</p>
                  </div>
                  <Badge variant="outline" className="text-xs">
                    {reg.market}
                  </Badge>
                </div>
                <p className="text-sm">{reg.requirement}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Disclaimer */}
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-6">
          <p className="text-xs text-amber-700 flex items-start gap-2">
            <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
            This tool provides general guidance on potential regulatory requirements. 
            Regulations change frequently. Always verify current requirements with official sources and consider professional advice.
          </p>
        </div>

        <div className="flex gap-3">
          <Button variant="outline" onClick={() => { setResult(null); setStep(1); }} className="flex-1">
            Start Over
          </Button>
          <Button onClick={onClose} className="flex-1 bg-brand hover:bg-brand-dark">
            Request Compliance Support
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-primary">Regulatory Exposure Checker</h2>
          <p className="text-sm text-muted-foreground">Step {step} of 3</p>
        </div>
        <Button variant="ghost" size="icon" onClick={onClose}>
          <X className="h-5 w-5" />
        </Button>
      </div>

      {/* Progress Bar */}
      <div className="w-full bg-slate-100 rounded-full h-2 mb-8">
        <div 
          className="bg-brand h-2 rounded-full transition-all duration-300"
          style={{ width: `${(step / 3) * 100}%` }}
        />
      </div>

      {renderStep()}
    </div>
  );
};

export default RegulatoryChecker;
